import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BsDownload } from "react-icons/bs";
import { Button, Container, Table } from "react-bootstrap";
import { connect } from "react-redux";
import { GetKmBadPings } from '../../../store/actions/reports/km-bad-pings';
import { GetSubscribedVehiclesByCIAction } from '../../../store/actions/vehicle/vehicle.js';
import api from "../../../service/api";
import API_BOOK from "../../../service/endpoints";
import { exportToExcel } from "../../../utils/export-to-excel";
import { getFormattedDate } from "../../../utils/common-methods";
import Loader from "../../helpers/hoc/loader/Loader";
import MultiSelect from "../../helpers/hoc/multi-select/MultiSelect";
import NoRecordFound from "../../helpers/hoc/noRecordFound";
import Pagination from "../../helpers/hoc/paginator/Pagination";
import { useVehicles } from '../../../context/VehicleContext';
import Filters from '../new-report/report-charging/child-components/Filters';
import "./css/HistoricDetails.css";

const KmBadPings = ({
  GetKmBadPings,
  GetSubscribedVehiclesByCIAction,
  subscribedVehicles,
  allKmBadPings,
  dataCount,
  isLoading,
}) => {
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [selectedOem, setSelectedOem] = useState([]);
  const [selectedModel, setSelectedModel] = useState([]);
  const [selectedCity, setSelectedCity] = useState([]);
  const [selectedVehicleType, setSelectedVehicleType] = useState([]);
  const [selectedHub, setSelectedHub] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [isRnFilterWarnEnabled, setIsRnFilterWarnEnabled] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isDtFilterWarnEnabled, setIsDtFilterWarnEnabled] = useState(false);
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;
  const { vehicles: mgVehicles } = useVehicles();

  const hubByRn = useMemo(() => {
    const map = {};
    (mgVehicles || []).forEach(v => {
      if (v.rn && v.mg?.depot_name) map[v.rn] = v.mg.depot_name;
    });
    return map;
  }, [mgVehicles]);

  useEffect(() => {
    if (!subscribedVehicles?.length) {
      GetSubscribedVehiclesByCIAction();
    }
  }, [GetSubscribedVehiclesByCIAction, subscribedVehicles]);

  const filterOptions = useMemo(() => {
    const oems = [], models = [], cities = [], types = [], hubs = [];
    const oemSet = new Set(), modelSet = new Set(), citySet = new Set(), typeSet = new Set(), hubSet = new Set();
    (subscribedVehicles || []).forEach(v => {
      if (v.oem && !oemSet.has(v.oem)) { oemSet.add(v.oem); oems.push({ label: v.oem, value: v.oem }); }
      if (v.model && !modelSet.has(v.model)) { modelSet.add(v.model); models.push({ label: v.model, value: v.model }); }
      if (v.location && !citySet.has(v.location)) { citySet.add(v.location); cities.push({ label: v.location, value: v.location }); }
      const typeKey = v.type ? v.type.replace(/\s+/g, '').toLowerCase() : '';
      if (typeKey && !typeSet.has(typeKey)) { typeSet.add(typeKey); types.push({ label: v.type.trim(), value: typeKey }); }
      const hub = hubByRn[v.registration_number];
      if (hub && !hubSet.has(hub)) { hubSet.add(hub); hubs.push({ label: hub, value: hub }); }
    });
    return { oems, models, cities, types, hubs };
  }, [subscribedVehicles, hubByRn]);

  const filteredRnOptions = useMemo(() => {
    if (!subscribedVehicles?.length) return [];
    return subscribedVehicles
      .filter(v => {
        if (selectedOem.length && !selectedOem.some(o => o.value === v.oem)) return false;
        if (selectedModel.length && !selectedModel.some(m => m.value === v.model)) return false;
        if (selectedCity.length && !selectedCity.some(c => c.value === v.location)) return false;
        if (selectedVehicleType.length && !selectedVehicleType.some(t => t.value === (v.type || '').replace(/\s+/g, '').toLowerCase())) return false;
        if (selectedHub.length && !selectedHub.some(h => h.value === hubByRn[v.registration_number])) return false;
        return true;
      })
      .map(v => ({ label: v.registration_number, value: v._id }));
  }, [subscribedVehicles, selectedOem, selectedModel, selectedCity, selectedVehicleType, selectedHub, hubByRn]);

  const cityByRn = useMemo(() => {
    const map = {};
    (subscribedVehicles || []).forEach(v => { if (v.registration_number) map[v.registration_number] = v.location || ''; });
    return map;
  }, [subscribedVehicles]);

  useEffect(() => {
    if (dataCount > 0) {
      GetKmBadPings({
        registrationNumbers: selectedOptions,
        fromDate: startDate,
        toDate: endDate,
        pageIndex: currentPage - 1,
        pageSize: ITEMS_PER_PAGE,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  function onDateChangeHandler({ startDate, endDate }) {
    setStartDate(startDate);
    setEndDate(endDate);
    setCurrentPage(1);
    if (selectedOptions && selectedOptions.length > 0) {
      GetKmBadPings({
        registrationNumbers: selectedOptions,
        fromDate: startDate,
        toDate: endDate,
        pageIndex: 0,
        pageSize: ITEMS_PER_PAGE,
      });
    }
  }

  // Stable ref so memoized Filters always calls latest handler
  const dateHandlerRef = useRef(onDateChangeHandler);
  dateHandlerRef.current = onDateChangeHandler;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableDateHandler = useCallback((params) => dateHandlerRef.current(params), []);

  // Memoize Filters to prevent re-renders from VehicleContext countdown (updates every second)
  const filtersElement = useMemo(() => (
    <Filters onDateChangeHandler={stableDateHandler}></Filters>
  ), [stableDateHandler]);

  function onSelectionChange() {
  }

  const onSubmitHandler = () => {
    if (!selectedOptions?.length) {
      setIsRnFilterWarnEnabled(true);
      setTimeout(() => { setIsRnFilterWarnEnabled(false); }, 2000);
      return;
    }
    if (!(endDate && startDate)) {
      setIsDtFilterWarnEnabled(true);
      setTimeout(() => { setIsDtFilterWarnEnabled(false); }, 2000);
      return;
    }
    setCurrentPage(1);
    GetKmBadPings({
      registrationNumbers: selectedOptions,
      fromDate: startDate,
      toDate: endDate,
      pageIndex: 0,
      pageSize: ITEMS_PER_PAGE,
    });
    setTimeout(() => { setIsSubmitClicked(true); }, 1000);
  };

  const handleExportExcel = async () => {
    try {
      const { GET_KM_BAD_PINGS } = API_BOOK.REPORTS.KM_REPORT;
      const resp = await api({
        method: GET_KM_BAD_PINGS.method,
        url: GET_KM_BAD_PINGS.url,
        data: {
          registrationNumbers: selectedOptions?.map((val) => val.label),
          fromDate: startDate,
          toDate: endDate,
          pageSize: 0,
          pageIndex: 0,
        }
      });
      if (resp.status === 200 && resp?.data?.data?.rows?.length) {
        const rows = resp.data.data.rows;
        const headers = [
          { header: 'S.No', key: 'sno', width: 8 },
          { header: 'Registration No', key: 'rn', width: 18 },
          { header: 'OEM', key: 'oem', width: 12 },
          { header: 'City', key: 'city', width: 15 },
          { header: 'Date', key: 'date', width: 12 },
          { header: 'Prev Timestamp', key: 'prevTs', width: 22 },
          { header: 'Bad Timestamp', key: 'badTs', width: 22 },
          { header: 'Prev OD', key: 'prevOd', width: 12 },
          { header: 'Bad OD', key: 'badOd', width: 12 },
          { header: 'OD Diff', key: 'odDiff', width: 12 },
          { header: 'Time Gap (min)', key: 'gapMin', width: 14 },
          { header: 'Max Possible', key: 'maxPossible', width: 14 },
          { header: 'Excess KM', key: 'excessKM', width: 12 },
          { header: 'Reason', key: 'reason', width: 15 },
          { header: 'Calculation', key: 'calculation', width: 20 },
        ];
        const data = rows.map((n, i) => ({
          sno: i + 1,
          rn: n.rn || '-',
          oem: n.oem || '-',
          city: cityByRn[n.rn] || '-',
          date: n.date || '-',
          prevTs: n.prevTs ? getFormattedDate(n.prevTs) : '-',
          badTs: n.badTs ? getFormattedDate(n.badTs) : '-',
          prevOd: n.prevOd != null ? Number(n.prevOd.toFixed(2)) : '-',
          badOd: n.badOd != null ? Number(n.badOd.toFixed(2)) : '-',
          odDiff: n.odDiff != null ? Number(n.odDiff.toFixed(2)) : '-',
          gapMin: n.gapMin != null ? Number(n.gapMin.toFixed(2)) : '-',
          maxPossible: n.maxPossible != null ? Number(n.maxPossible.toFixed(2)) : '-',
          excessKM: n.excessKM != null ? Number(n.excessKM.toFixed(2)) : '-',
          reason: n.reason || '-',
          calculation: n.calculation || '-',
        }));
        exportToExcel({ data, headers, fileName: 'KM_Bad_Pings.xlsx', sheetName: 'Bad Pings' });
      }
    } catch (error) {
      console.log('Export error:', error);
    }
  };

  return (
    <>
      <Container fluid>
        <div className="km-filter-row">
          <div className="km-filter-item">
            <MultiSelect
              isMultiSelect={true}
              options={filterOptions.oems}
              selectedOptions={selectedOem}
              setSelectedOptions={setSelectedOem}
              placeholder="OEM"
              placeholderColor='#C6C6C6'
            />
          </div>
          <div className="km-filter-item">
            <MultiSelect
              isMultiSelect={true}
              options={filterOptions.models}
              selectedOptions={selectedModel}
              setSelectedOptions={setSelectedModel}
              placeholder="Model"
              placeholderColor='#C6C6C6'
            />
          </div>
          <div className="km-filter-item">
            <MultiSelect
              isMultiSelect={true}
              options={filterOptions.cities}
              selectedOptions={selectedCity}
              setSelectedOptions={setSelectedCity}
              placeholder="City"
              placeholderColor='#C6C6C6'
            />
          </div>
          <div className="km-filter-item">
            <MultiSelect
              isMultiSelect={true}
              options={filterOptions.types}
              selectedOptions={selectedVehicleType}
              setSelectedOptions={setSelectedVehicleType}
              placeholder="Vehicle Type"
              placeholderColor='#C6C6C6'
            />
          </div>
          <div className="km-filter-item">
            <MultiSelect
              isMultiSelect={true}
              options={filterOptions.hubs}
              selectedOptions={selectedHub}
              setSelectedOptions={setSelectedHub}
              placeholder="Hub"
              placeholderColor='#C6C6C6'
            />
          </div>
        </div>

        <div className="col-12 historic-details-filter-layout">
          <div className={`multi-select-width ${!selectedOptions[0] ? 'no-value-selected' : ''}`}>
            <MultiSelect
              isMultiSelect={true}
              setNoDataWarning={isRnFilterWarnEnabled}
              options={filteredRnOptions}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              onChange={onSelectionChange}
              placeholderColor='#C6C6C6'
              customBackgroundColor={{ backgroundColor: selectedOptions[0] ? "#FFFFFF" : "red" }}
            />
          </div>

          <div>
            <Button
              className="bttn height"
              id="getReport"
              type="submit"
              onClick={onSubmitHandler}
              disabled={!selectedOptions?.length || !endDate || !startDate}
            >
              Get Report
            </Button>
          </div>

          <div>
            <button
              disabled={!allKmBadPings?.length || !selectedOptions?.length || !endDate || !startDate}
              className={
                !allKmBadPings?.length || !selectedOptions?.length || !endDate || !startDate
                  ? "dwn-btn dwn-btn-disabled"
                  : "dwn-btn"
              }
              onClick={handleExportExcel}
            >
              <BsDownload style={{ width: '22px', height: '22px' }} />
            </button>
          </div>

          <div className='col-4 date__picker'>
            {filtersElement}
          </div>
        </div>

        <div className="bg-white mb-2 mt-4 position-relative trip__log__table historic-details-table-wrapper">
          <Table responsive={true} borderless className="mb-0" id="km-bad-pings-table">
            <thead>
              <tr className="th-border-bottom position-relative">
                <th className="ps-2 text-nowrap"><p className="fw500">S.No</p></th>
                <th className="ps-2 text-nowrap"><p className="fw500">Registration No</p></th>
                <th className="ps-2 text-nowrap"><p className="fw500">OEM</p></th>
                <th className="ps-2 text-nowrap"><p className="fw500">City</p></th>
                <th className="ps-2 text-nowrap"><p className="fw500">Date</p></th>
                <th className="ps-2 text-nowrap"><p className="fw500">Prev Timestamp</p></th>
                <th className="ps-2 text-nowrap"><p className="fw500">Bad Timestamp</p></th>
                <th className="ps-2 text-nowrap"><p className="fw500">Prev OD</p></th>
                <th className="ps-2 text-nowrap"><p className="fw500">Bad OD</p></th>
                <th className="ps-2 text-nowrap"><p className="fw500">OD Diff</p></th>
                <th className="ps-2 text-nowrap"><p className="fw500">Time Gap<br />(min)</p></th>
                <th className="ps-2 text-nowrap"><p className="fw500">Max Possible</p></th>
                <th className="ps-2 text-nowrap"><p className="fw500">Excess KM</p></th>
                <th className="ps-2 text-nowrap"><p className="fw500">Reason</p></th>
                <th className="ps-2 text-nowrap"><p className="fw500">Calculation</p></th>
              </tr>
            </thead>
            <tbody>
              {allKmBadPings?.length ? allKmBadPings.map((n, index) => (
                <tr key={index}>
                  <td>{index + 1 + ((currentPage - 1) * ITEMS_PER_PAGE) + "."}</td>
                  <td>{n?.rn || '-'}</td>
                  <td>{n?.oem || '-'}</td>
                  <td>{cityByRn[n?.rn] || '-'}</td>
                  <td>{n?.date || '-'}</td>
                  <td>{n?.prevTs ? getFormattedDate(n.prevTs) : '-'}</td>
                  <td>{n?.badTs ? getFormattedDate(n.badTs) : '-'}</td>
                  <td>{n?.prevOd != null ? n.prevOd.toFixed(2) : '-'}</td>
                  <td>{n?.badOd != null ? n.badOd.toFixed(2) : '-'}</td>
                  <td>{n?.odDiff != null ? n.odDiff.toFixed(2) : '-'}</td>
                  <td>{n?.gapMin != null ? n.gapMin.toFixed(2) : '-'}</td>
                  <td>{n?.maxPossible != null ? n.maxPossible.toFixed(2) : '-'}</td>
                  <td>{n?.excessKM != null ? n.excessKM.toFixed(2) : '-'}</td>
                  <td>{n?.reason || '-'}</td>
                  <td>{n?.calculation || '-'}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="15" className="text-center py-4" style={{ color: '#999' }}>No data</td>
                </tr>
              )}
            </tbody>
          </Table>
          {allKmBadPings?.length > 0 && (
            <Pagination
              disabled={false}
              totalPages={Math.ceil((dataCount || 0) / ITEMS_PER_PAGE)}
              currentPage={currentPage}
              setPage={setCurrentPage}
            />
          )}
        </div>
        {!allKmBadPings?.length && isSubmitClicked && !isLoading ? (
          <NoRecordFound></NoRecordFound>
        ) : (
          <></>
        )}
      </Container>
      <Loader isLoading={isLoading} />
    </>
  );
};

KmBadPings.propTypes = {
  allKmBadPings: PropTypes.array.isRequired,
  subscribedVehicles: PropTypes.array.isRequired,
  dataCount: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  GetKmBadPings: PropTypes.func.isRequired,
  GetSubscribedVehiclesByCIAction: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  allKmBadPings: state.kmBadPings.allKmBadPings,
  subscribedVehicles: state.vehicle.subscribedVehiclesByCI,
  dataCount: state.kmBadPings.dataCount,
  isLoading: state.loader.isLoading,
});

export default connect(mapStateToProps, {
  GetKmBadPings,
  GetSubscribedVehiclesByCIAction,
})(KmBadPings);
